import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ayarAPI, caseFileAPI, documentAPI } from '../../api/api';
import MuvekkilPortal from '../../pages/MuvekkilPortal';
import '../../pages/MuvekkilPortal.css';
import './MuvekkilGorunumu.css';

/**
 * "Müvekkil gözünden gör" penceresi.
 *
 * Avukatın, bir dosyanın müvekkil portalında NASIL göründüğünü birebir
 * görebilmesi için. Veriyi müvekkil portalıyla AYNI projeksiyondan alır
 * (Muvekkil/onizleme ucu), böylece burada görünen şey müvekkilin gerçekten
 * gördüğü şeydir — ayrı bir "tahmin" değildir.
 *
 * Portal ile aynı CSS sınıfları kullanılır (MuvekkilPortal.css), görünüm
 * ikiye ayrılmasın diye.
 */

const tarih = (d) => {
  if (!d) return null;
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return null;
  return t.toLocaleDateString('tr-TR');
};

/** User-Agent metninden okunabilir kısa bir cihaz adı çıkarır. */
const cihazAdi = (ua) => {
  if (!ua) return null;
  const s = ua.toLowerCase();
  if (s.includes('iphone')) return 'iPhone';
  if (s.includes('ipad')) return 'iPad';
  if (s.includes('android')) return 'Android';
  if (s.includes('macintosh') || s.includes('mac os')) return 'Mac';
  if (s.includes('windows')) return 'Windows';
  return 'Diğer';
};

/** İşleme göre renk sınıfı — her tür kendi zemin ve şerit rengini alır. */
const logSinifi = (islem) => {
  switch (islem) {
    case 'Giris': return 'mg-log-giris';
    case 'GirisBasarisiz':
    case 'GirisDurduruldu': return 'mg-log-hata';
    case 'Cikis': return 'mg-log-cikis';
    case 'BelgeGoruntuleme': return 'mg-log-inceleme';
    case 'BelgeIndirme': return 'mg-log-indirme';
    case 'BelgeYukleme': return 'mg-log-yukleme';
    default: return 'mg-log-diger';
  }
};

/** Yalnızca saat (gün başlığı zaten tarihi veriyor). */
const saat = (d) => {
  if (!d) return '';
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return '';
  return t.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
};

/** Gruplama anahtarı: YYYY-AA-GG */
const gunAnahtari = (d) => {
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return 'bilinmiyor';
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
};

/** Gün başlığı: bugün / dün ya da tam tarih. */
const gunBasligi = (anahtar) => {
  if (anahtar === 'bilinmiyor') return 'Tarihi bilinmeyen';
  const [y, a, g] = anahtar.split('-').map(Number);
  const t = new Date(y, a - 1, g);
  const bugun = new Date(); bugun.setHours(0, 0, 0, 0);
  const fark = Math.round((bugun - t) / 86400000);
  if (fark === 0) return 'Bugün';
  if (fark === 1) return 'Dün';
  return t.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
};

/** Kayıtları güne göre gruplar; sıra korunur (yeniden eskiye). */
const gunlereBol = (kayitlar) => {
  const gruplar = [];
  const dizin = new Map();
  for (const k of kayitlar) {
    const anahtar = gunAnahtari(k.tarih);
    if (!dizin.has(anahtar)) {
      const grup = { anahtar, baslik: gunBasligi(anahtar), kayitlar: [] };
      dizin.set(anahtar, grup);
      gruplar.push(grup);
    }
    dizin.get(anahtar).kayitlar.push(k);
  }
  return gruplar;
};

const para = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₺';
};

const kalanGun = (d) => {
  if (!d) return null;
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return null;
  const bugun = new Date();
  bugun.setHours(0, 0, 0, 0);
  t.setHours(0, 0, 0, 0);
  const fark = Math.round((t - bugun) / 86400000);
  return fark >= 0 ? fark : null;
};

const Alanlar = ({ baslik, satirlar }) => {
  const dolu = satirlar.filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (dolu.length === 0) return null;
  return (
    <section className="mp-blok">
      <h4>{baslik}</h4>
      <dl className="mp-alanlar">
        {dolu.map(([etiket, deger]) => (
          <div key={etiket}>
            <dt>{etiket}</dt>
            <dd>{deger}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

const MuvekkilGorunumu = ({ caseFileId, onKapat }) => {
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [loglar, setLoglar] = useState(null);
  const [loglarYukleniyor, setLoglarYukleniyor] = useState(true);
  const [kapaliGunler, setKapaliGunler] = useState(() => new Set());
  // Kayıt paneli varsayılan olarak KAPALI; sayfada yer kaplamasın.
  // Açıldığında soldan içeri kayar.
  const [loglarAcik, setLoglarAcik] = useState(false);

  const gunAcKapa = (anahtar) => {
    setKapaliGunler((eski) => {
      const yeni = new Set(eski);
      if (yeni.has(anahtar)) yeni.delete(anahtar); else yeni.add(anahtar);
      return yeni;
    });
  };

  useEffect(() => {
    let iptal = false;
    (async () => {
      setYukleniyor(true);
      setHata(null);
      try {
        const d = await caseFileAPI.muvekkilOnizleme(caseFileId);
        if (!iptal) setVeri(d);
      } catch (err) {
        if (!iptal) setHata(err?.response?.data?.message || 'Görünüm alınamadı.');
      } finally {
        if (!iptal) setYukleniyor(false);
      }
    })();
    return () => { iptal = true; };
  }, [caseFileId]);

  /**
   * Portalin bekledigi veri kaynagi — personel yetkisiyle.
   *
   * Portal normalde muvekkilAPI kullanir. Burada ayni metot adlarini
   * personel uclarina bagliyoruz; portal aradaki farki bilmiyor ve
   * ekrani muvekkile cizdigi gibi ciziyor.
   *
   * Belge indirme personel ucundan gecer: boylece avukatin onizlemesi
   * muvekkilin hareket kayitlarina "goruntuledi" diye dusmez.
   */
  const onizlemeApi = useMemo(() => ({
    dosyalarim: async () => (veri?.dosya ? [veri.dosya] : []),
    belgeler: async () => veri?.belgeler ?? [],
    odemeler: async () => veri?.odemeler ?? null,
    iletisim: async () => {
      try {
        const a = await ayarAPI.whatsappGetir();
        return { hazir: !!(a?.hazir ?? a?.Hazir), whatsapp: a?.whatsapp ?? a?.Whatsapp ?? null };
      } catch {
        return { hazir: false, whatsapp: null };
      }
    },
    belgeIndir: async (documentId) => documentAPI.download(documentId),
    // Akis ucu yalnizca muvekkil tarafinda var; portal bunu gorunce
    // indirme yoluna duser (bkz. MuvekkilPortal icindeki akisAdresi).
    akisUrl: undefined,
    belgeYukle: async () => { throw new Error('Önizlemede belge yüklenemez.'); },
    // Bildirimler muvekkilin kendi kayitlari; onizlemede bos gosteriliyor.
    // Personelin kendi ekraninda muvekkilin bildirim gecmisini acmak
    // gerekmiyor, zil dugmesi de bu yuzden bos gorunuyor.
    bildirimler: async () => ({ bildirimler: [], okunmamis: 0 }),
    bildirimOkundu: async () => ({ ok: true, okunmamis: 0 }),
    cikis: () => {},
  }), [veri]);

  // Hareket kayıtları — yalnızca personel görebilir (uç personel token'ı ister).
  useEffect(() => {
    let iptal = false;
    (async () => {
      setLoglarYukleniyor(true);
      try {
        const d = await caseFileAPI.muvekkilLoglari(caseFileId);
        if (!iptal) setLoglar(d);
      } catch (_) {
        if (!iptal) setLoglar(null);
      } finally {
        if (!iptal) setLoglarYukleniyor(false);
      }
    })();
    return () => { iptal = true; };
  }, [caseFileId]);

  useEffect(() => {
    const tus = (e) => {
      if (e.key === 'Escape') onKapat();
    };
    window.addEventListener('keydown', tus);
    return () => window.removeEventListener('keydown', tus);
  }, [onKapat]);

  const dosya = veri?.dosya;
  const kalan = dosya ? kalanGun(dosya.durusmaTarihi) : null;
  const buroBelgeleri = (veri?.belgeler || []).filter((b) => !b.muvekkilYukledi);
  const muvekkilBelgeleri = (veri?.belgeler || []).filter((b) => b.muvekkilYukledi);

  return createPortal(
    <div className="mg-katman" role="dialog" aria-modal="true" onClick={onKapat}>
      <div className="mg-kutu" onClick={(e) => e.stopPropagation()}>
        <div className="mg-ust">
          <div className="mg-ust-sol">
            <span className="mg-rozet">MÜVEKKİL GÖZÜNDEN</span>
            <strong>
              {dosya ? `Föy ${dosya.foyNo} — ${dosya.muvekkil || ''}` : 'Yükleniyor…'}
            </strong>
          </div>
          <button type="button" className="mg-kapat" onClick={onKapat} aria-label="Kapat">×</button>
        </div>

        <div className="mg-govde">
          {yukleniyor && <p className="mp-bilgi">Yükleniyor…</p>}
          {hata && <p className="mp-uyari">{hata}</p>}

          {veri?.muvekkileKapali && (
            <div className="mg-uyari-ozel">
              Bu dosya <strong>özel</strong> olarak işaretli. Müvekkil portalında hiç görünmez;
              aşağıdaki görünüm yalnızca bilgi amaçlıdır.
            </div>
          )}

          {dosya && (
            <div className="mg-duzen">
              {/* ---- Soldan açılan hareket kayıtları çekmecesi ---- */}
              {loglarAcik && (
                <div className="mg-log-perde" onClick={() => setLoglarAcik(false)} aria-hidden="true" />
              )}

              <aside className={`mg-loglar ${loglarAcik ? 'acik' : ''}`}>
                <div className="mg-loglar-ust">
                  <div>
                    <h3>Hareket Kayıtları</h3>
                    <span>Yalnızca avukat ve yöneticiler görür</span>
                  </div>
                  <button
                    type="button"
                    className="mg-loglar-kapat"
                    onClick={() => setLoglarAcik(false)}
                    aria-label="Kayıtları kapat"
                  >
                    ×
                  </button>
                </div>

                {loglarYukleniyor ? (
                  <p className="mg-log-bos">Yükleniyor…</p>
                ) : !loglar || loglar.adet === 0 ? (
                  <p className="mg-log-bos">
                    Bu dosya için henüz hareket kaydı yok. Müvekkil giriş yaptığında,
                    belge indirdiğinde veya yüklediğinde burada görünecek.
                  </p>
                ) : (
                  <div className="mg-gunler">
                    {gunlereBol(loglar.kayitlar).map((grup) => {
                      const kapali = kapaliGunler.has(grup.anahtar);
                      return (
                        <section key={grup.anahtar} className="mg-gun">
                          <button
                            type="button"
                            className={`mg-gun-baslik ${kapali ? 'kapali' : ''}`}
                            onClick={() => gunAcKapa(grup.anahtar)}
                            aria-expanded={!kapali}
                          >
                            <span className="mg-gun-ok" aria-hidden="true">▾</span>
                            <span className="mg-gun-ad">{grup.baslik}</span>
                            <span className="mg-gun-adet">{grup.kayitlar.length}</span>
                          </button>

                          {!kapali && (
                            <ul className="mg-log-liste">
                              {grup.kayitlar.map((k) => (
                                <li key={k.id} className={logSinifi(k.islem)}>
                                  <div className="mg-log-satir">
                                    <strong>{k.etiket}</strong>
                                    <span className="mg-log-tarih">{saat(k.tarih)}</span>
                                  </div>
                                  {(k.belgeAdi || k.aciklama) && (
                                    <p className="mg-log-aciklama">{k.belgeAdi || k.aciklama}</p>
                                  )}
                                  {(k.ip || k.cihaz) && (
                                    <p className="mg-log-kaynak">
                                      {cihazAdi(k.cihaz)}{k.ip ? ` · ${k.ip}` : ''}
                                    </p>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </section>
                      );
                    })}
                  </div>
                )}
              </aside>

              {/* ---- Müvekkilin gördüğü ekran ----
                   Taklit DEGIL: muvekkilin gercekten gordugu portal bileseninin
                   ta kendisi. Veri, personel yetkisiyle calisan onizleme ucundan
                   geliyor; portal onu muvekkilin gordugu bicimde ciziyor.
                   Boylece portal her degistiginde bu pencere kendiliginden ayni
                   kalir — daha once burada ayri bir taklit ekran vardi ve portal
                   gelistikce geride kaliyordu. ---- */}
              <div className="mp-detay mg-icerik">
                <button
                  type="button"
                  className="mg-loglar-ac"
                  onClick={() => setLoglarAcik(true)}
                >
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
                       strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 6h16M4 12h16M4 18h10" />
                  </svg>
                  Hareket Kayıtları
                  {loglar?.adet > 0 && <span className="mg-loglar-ac-adet">{loglar.adet}</span>}
                </button>

                <MuvekkilPortal
                  api={onizlemeApi}
                  onizlemeModu
                  bilgiDisaridan={{ adSoyad: veri?.dosya?.muvekkil || '' }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

    </div>,
    document.body
  );
};

export default MuvekkilGorunumu;
